import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Radio, Select, Table, Image, Tag, Alert, Space } from 'antd';
import InputList from '../../commonComponents/InputList';
import { useGetCompanyListMinimal } from '../../hooks/useCompanies';
import { useLookupSkusForMerge } from '../../hooks/useGuideline';

function parseSkuPaste(text) {
    return [...new Set(String(text || '').split(/[\n\r,;\t]+/).map(s => s.trim()).filter(Boolean))];
}

function skuImageUrl(sku) {
    return sku?.articleImage?.thumbPath?.url || sku?.articleImage?.imagePath?.url || sku?.tagImage?.thumbPath?.url || null;
}

function skuPreviewUrl(sku) {
    return sku?.articleImage?.imagePath?.url || skuImageUrl(sku);
}

function FormInputList({ value, onChange }) {
    return (
        <InputList
            links={Array.isArray(value) ? value : []}
            UpdateItems={next => onChange((next || []).filter(d => d !== ''))}
        />
    );
}

function apiErrorMessage(error, fallback) {
    const data = error?.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') {
        try {
            return JSON.parse(data).message || fallback;
        } catch {
            return data;
        }
    }
    return data.message || fallback;
}

export default function CreateMergeGuideline({
    HandleOnOK,
    onCancel,
    showCreate,
    loading,
    name = '',
    description = '',
    links = null,
    excludedLinks = null,
}) {
    const [form] = Form.useForm();
    const [mode, setMode] = useState('links');
    const [skuText, setSkuText] = useState('');
    const [companyCode, setCompanyCode] = useState(null);
    const [found, setFound] = useState([]);
    const [notFound, setNotFound] = useState([]);
    const [lookupError, setLookupError] = useState('');
    const { data: companies, isFetching: companiesLoading } = useGetCompanyListMinimal();
    const lookupMutation = useLookupSkusForMerge();

    useEffect(() => {
        form.setFieldsValue({
            name,
            description,
            links: Array.isArray(links) ? links : [],
            ExcludeLinks: Array.isArray(excludedLinks) ? excludedLinks : [],
        });
    }, [name, description, form, links, excludedLinks]);

    const resetLookup = () => {
        setFound([]);
        setNotFound([]);
        setLookupError('');
    };

    const lookupSkus = async () => {
        const identifiers = parseSkuPaste(skuText);
        if (!companyCode) {
            setLookupError('Select a client');
            return;
        }
        if (identifiers.length === 0) {
            setLookupError('Paste at least one SKU');
            return;
        }
        setLookupError('');
        try {
            const data = await lookupMutation.mutateAsync([companyCode, identifiers]);
            setFound(data?.found || []);
            setNotFound(data?.notFound || []);
        } catch (error) {
            resetLookup();
            setLookupError(apiErrorMessage(error, 'Failed to look up SKUs'));
        }
    };

    const onFinish = values => {
        if (mode === 'skus') {
            HandleOnOK({
                name: values.name,
                description: values.description,
                mode: 'skus',
                skuIds: found.map(sku => sku._id),
            });
            return;
        }
        HandleOnOK({ ...values, mode: 'links' });
    };

    const createDisabled = loading || (mode === 'skus' && found.length === 0);

    return (
        <Modal
            open={showCreate}
            onCancel={onCancel}
            width="80%"
            title="Create Merged Guideline"
            style={{ top: '20px' }}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button key="create" type="primary" onClick={() => form.submit()} disabled={createDisabled} loading={loading}>
                    Create
                </Button>,
            ]}
        >
            <div style={{ height: '80vh', overflow: 'auto' }}>
                <Radio.Group
                    value={mode}
                    onChange={e => {
                        setMode(e.target.value);
                        resetLookup();
                    }}
                    style={{ marginBottom: 16 }}
                    optionType="button"
                    buttonStyle="solid"
                >
                    <Radio.Button value="links">From guideline links</Radio.Button>
                    <Radio.Button value="skus">From SKU list</Radio.Button>
                </Radio.Group>

                <Form
                    form={form}
                    onFinish={onFinish}
                    layout="vertical"
                    initialValues={{ name: '', description: '', links: [], ExcludeLinks: [] }}
                >
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{ required: true, message: 'Please Specify a name for the new Guideline' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Description"
                        name="description"
                        rules={[{ required: true, message: 'Please Specify a description for the new Guideline' }]}
                    >
                        <Input />
                    </Form.Item>

                    {mode === 'links' ? (
                        <>
                            <Form.Item
                                label="Add From Links"
                                name="links"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            const filled = (Array.isArray(value) ? value : []).filter(Boolean);
                                            if (filled.length === 0) {
                                                return Promise.reject(
                                                    new Error('Please Specify a List of Shoot guideline Links. At least 1 link is required to merge')
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <FormInputList />
                            </Form.Item>
                            <Form.Item label="Exclude from Links" name="ExcludeLinks">
                                <FormInputList />
                            </Form.Item>
                        </>
                    ) : (
                        <>
                            <Form.Item label="Client" required>
                                <Select
                                    showSearch
                                    allowClear
                                    loading={companiesLoading}
                                    placeholder="Select client to search SKUs"
                                    optionFilterProp="searchBy"
                                    value={companyCode}
                                    onChange={value => {
                                        setCompanyCode(value || null);
                                        resetLookup();
                                    }}
                                    options={(Array.isArray(companies) ? companies : []).map(company => ({
                                        label: `${company.code} — ${company.name}`,
                                        value: company.code,
                                        searchBy: `${company.code} ${company.name}`,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item label="SKU list" required>
                                <Input.TextArea
                                    rows={6}
                                    value={skuText}
                                    onChange={e => {
                                        setSkuText(e.target.value);
                                        resetLookup();
                                    }}
                                    placeholder="Paste SKUs from Excel — one per line, or comma / tab separated. Matches sku name or barcode."
                                />
                            </Form.Item>
                            <Space style={{ marginBottom: 16 }}>
                                <Button type="primary" onClick={lookupSkus} loading={lookupMutation.isLoading}>
                                    Look up SKUs
                                </Button>
                                <span>{parseSkuPaste(skuText).length} pasted</span>
                            </Space>
                            {lookupError ? <Alert type="error" message={lookupError} style={{ marginBottom: 16 }} /> : null}
                            {notFound.length > 0 ? (
                                <Alert
                                    type="warning"
                                    style={{ marginBottom: 16 }}
                                    message={`${notFound.length} not found in ${companyCode}`}
                                    description={notFound.map(id => (
                                        <Tag key={id}>{id}</Tag>
                                    ))}
                                />
                            ) : null}
                            {found.length > 0 ? (
                                <Table
                                    size="small"
                                    rowKey={row => String(row._id)}
                                    dataSource={found}
                                    pagination={false}
                                    scroll={{ y: 360 }}
                                    columns={[
                                        {
                                            title: 'Image',
                                            width: 80,
                                            render: (_, sku) => {
                                                const src = skuImageUrl(sku);
                                                return src ? (
                                                    <Image
                                                        src={src}
                                                        width={48}
                                                        height={64}
                                                        style={{ objectFit: 'cover' }}
                                                        alt={sku.skuName}
                                                        preview={{ src: skuPreviewUrl(sku) }}
                                                    />
                                                ) : (
                                                    '—'
                                                );
                                            },
                                        },
                                        { title: 'SKU', dataIndex: 'skuName' },
                                        {
                                            title: 'Barcodes',
                                            render: (_, sku) =>
                                                (sku.barcodes || []).length ? sku.barcodes.map(code => <Tag key={code}>{code}</Tag>) : '—',
                                        },
                                        { title: 'EAN', dataIndex: 'ean', render: value => value || '—' },
                                        { title: 'Order', dataIndex: 'orderName', render: value => value || '—' },
                                        {
                                            title: 'Matched',
                                            dataIndex: 'query',
                                            render: value => <Tag color="blue">{value}</Tag>,
                                        },
                                        {
                                            title: '',
                                            width: 70,
                                            render: (_, sku) => (
                                                <Button
                                                    type="link"
                                                    onClick={() => setFound(prev => prev.filter(row => String(row._id) !== String(sku._id)))}
                                                >
                                                    Remove
                                                </Button>
                                            ),
                                        },
                                    ]}
                                />
                            ) : null}
                        </>
                    )}
                </Form>
            </div>
        </Modal>
    );
}
